import React from "react";

import { captureException } from "@/lib/analytics";

interface State {
  failed: boolean;
}

/**
 * Last line of defence for a render crash.
 *
 * Without it one throwing component blanks the whole iframe and nobody hears
 * about it. Deliberately plain and unbranded: it renders outside the theme and
 * i18n providers, and sub-accounts must not see vendor names.
 */
export default class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    captureException(error, { component_stack: info.componentStack, source: "error_boundary" });
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-gray-50 px-6 text-center">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">Something went wrong</h1>
          <p className="mt-2 text-sm text-gray-500">
            The page hit an unexpected error. Reloading usually fixes it.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-5 rounded-lg bg-gray-800 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-700"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
